import {clsx} from 'clsx'
import {useMemo, useRef} from 'react'

import {useMarkerNavigation} from '../../hooks/use-marker-navigation'
import type {CommentAuthor, Thread} from '../../conversation-types'
import {FileReviewThread} from './FileReviewThread'
import {ReviewThreadWithoutData} from './ReviewThreadWithoutData'
import styles from './InlineMarkers.module.css'
import type {InlineReviewThreadProps} from './InlineReviewThread'

export interface FileMarkersProps extends Pick<InlineReviewThreadProps, 'viewerData'> {
  conversationListThreads: Thread[]
  filePath: string
  manuallyUpdateCommentsWithThisThreadId?: string
  ghostUser?: CommentAuthor
}

export function FileMarkers({
  conversationListThreads,
  filePath,
  manuallyUpdateCommentsWithThisThreadId,
  ghostUser,
  ...rest
}: FileMarkersProps) {
  // Split threads into those with data (conversation list) and without data
  const threadsWithData = useMemo(
    () => conversationListThreads.filter(thread => !thread.hideUntilLoadedAndInView),
    [conversationListThreads],
  )
  const threadsWithoutData = useMemo(
    () => conversationListThreads.filter(thread => thread.hideUntilLoadedAndInView),
    [conversationListThreads],
  )

  // Create a ref for the markers container
  const markersContainerRef = useRef<HTMLDivElement>(null)

  // Use our marker navigation hook
  useMarkerNavigation({
    containerRef: markersContainerRef,
    markers: [...threadsWithData, ...threadsWithoutData],
    disabled: false,
    focusInStrategy: 'closest',
  })
  return (
    <div className={'d-flex pt-1 px-1'}>
      <div className={styles.fileMarkersWrapper} ref={markersContainerRef}>
        {threadsWithData.map((thread, i) => (
          <div
            key={`review-thread-${thread.id}`}
            className={clsx(
              'mt-1 border rounded-2 color-border-default color-shadow-small',
              i === threadsWithData.length - 1 && threadsWithoutData.length === 0 ? 'mb-1' : 'mb-2',
            )}
            data-first-marker={i === 0}
            data-marker-id={`${thread.id}`} // Add data-marker-id attribute for keyboard navigation
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0} // Make the marker focusable
          >
            <FileReviewThread
              key={thread.id}
              viewerData={rest.viewerData}
              filePath={filePath}
              thread={thread}
              ghostUser={ghostUser}
            />
          </div>
        ))}
        {threadsWithoutData.map((thread, i) => {
          const isFirstMarker = threadsWithData.length === 0 && i === 0
          const threadsCount = threadsWithoutData.length
          const isLastThread = i === threadsCount - 1

          return (
            <ReviewThreadWithoutData
              key={`file-review-thread-without-data-${thread.id}`}
              thread={thread}
              isFirstMarker={isFirstMarker}
              isLastThread={isLastThread}
              isInDialogMode={false}
            />
          )
        })}
      </div>
    </div>
  )
}
